import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, Navigation, Phone, LogOut, Power, Loader2,
  CheckCircle, XCircle, Clock, AlertCircle, History, Briefcase,
  Bell, BellRing, Volume2
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useProvider } from '../context/ProviderContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import L from 'leaflet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom map icons
const customerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const providerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Generate a short two-tone alert beep using Web Audio API
const playAlertBeep = (audioCtx) => {
  if (!audioCtx) return;
  try {
    const now = audioCtx.currentTime;
    const tones = [
      { freq: 880, start: 0, dur: 0.18 },
      { freq: 1320, start: 0.22, dur: 0.18 },
      { freq: 880, start: 0.44, dur: 0.18 },
    ];
    tones.forEach(({ freq, start, dur }) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.4, now + start + 0.02);
      gain.gain.linearRampToValueAtTime(0, now + start + dur);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    });
  } catch (e) {
    console.warn('Audio playback failed:', e);
  }
};

export const ProviderDashboard = () => {
  const { provider, logout, updateProvider } = useProvider();
  const navigate = useNavigate();

  const isAvailable = provider?.is_available || false;
  const [currentLocation, setCurrentLocation] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [jobHistory, setJobHistory] = useState([]);
  const [statistics, setStatistics] = useState(null);

  // Alerts
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [activeAlert, setActiveAlert] = useState(null); // { notification_id, title, message, request_number }
  const audioCtxRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const activeJobRef = useRef(null);

  // Keep ref synced for use inside watchPosition closure (avoids stale state)
  useEffect(() => {
    activeJobRef.current = activeJob;
  }, [activeJob]);

  // Whenever location or active job changes, push location to backend so the
  // customer's tracking map shows the mechanic in real-time. Sharing is gated
  // ONLY by the presence of an active job — not availability — because
  // assigned providers are marked is_available=false (busy) but still need
  // to share location during the job.
  useEffect(() => {
    if (currentLocation && activeJob) {
      shareLocation(currentLocation);
    }
    // eslint-disable-next-line
  }, [currentLocation, activeJob?.request_number]);

  useEffect(() => {
    if (!provider) {
      navigate('/provider/login');
      return;
    }

    startLocationTracking();
    fetchData();
    fetchJobHistory();

    const interval = setInterval(() => {
      fetchData();
      pollNotifications();
    }, 5000);
    return () => clearInterval(interval);
  }, [provider]);

  const startLocationTracking = () => {
    if (navigator.geolocation) {
      navigator.geolocation.watchPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setCurrentLocation(location);
        },
        (error) => console.error('Location error:', error),
        { enableHighAccuracy: true, maximumAge: 5000 }
      );
    }
  };

  const shareLocation = async (location) => {
    const job = activeJobRef.current;
    if (!job) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/roadside/provider/location`,
        { latitude: location.latitude, longitude: location.longitude },
        { params: { provider_id: provider.provider_id } }
      );
    } catch (error) {
      console.error('Error sharing location:', error);
    }
  };

  const fetchData = async () => {
    try {
      const assignedJobs = await axios.get(
        `${BACKEND_URL}/api/roadside/admin/all-requests`
      );
      const myJobs = (assignedJobs.data.requests || []).filter(
        (r) =>
          r.provider_id === provider.provider_id &&
          ['Assigned', 'En Route', 'Arrived', 'In Progress'].includes(r.status)
      );

      if (myJobs.length > 0) {
        setActiveJob(myJobs[0]);
        setPendingRequests([]);
      } else {
        setActiveJob(null);
        if (isAvailable) {
          const availableResponse = await axios.get(
            `${BACKEND_URL}/api/roadside/provider/available-jobs`,
            { params: { provider_id: provider.provider_id } }
          );
          setPendingRequests(availableResponse.data || []);
        } else {
          setPendingRequests([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchJobHistory = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/roadside/provider/job-history`,
        { params: { provider_id: provider.provider_id } }
      );
      setJobHistory(response.data.jobs || []);
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error fetching job history:', error);
    }
  };

  const pollNotifications = useCallback(async () => {
    if (!provider) return;
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/roadside/provider/notifications`,
        { params: { provider_id: provider.provider_id, only_unseen: true } }
      );
      const notifications = res.data.notifications || [];
      // Trigger alert only for new ones not yet handled this session
      const newOnes = notifications.filter(
        (n) => !seenIdsRef.current.has(n.notification_id)
      );
      if (newOnes.length === 0) return;

      newOnes.forEach((n) => seenIdsRef.current.add(n.notification_id));

      // Show banner for the most recent notification (always)
      const latest = newOnes[0];
      setActiveAlert(latest);

      // Sound + vibration only if user enabled alerts AND mechanic is online
      if (alertsEnabled && isAvailable) {
        playAlertBeep(audioCtxRef.current);
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          navigator.vibrate([300, 120, 300]);
        }
      }
    } catch (error) {
      console.error('Error polling notifications:', error);
    }
  }, [provider, isAvailable, alertsEnabled]);

  const dismissAlert = () => {
    if (!activeAlert) return;
    const notifId = activeAlert.notification_id;
    // Clear immediately so polling doesn't re-show it before API completes
    seenIdsRef.current.add(notifId);
    setActiveAlert(null);
    // Fire-and-forget mark-seen so the banner stays gone after refresh
    axios
      .post(
        `${BACKEND_URL}/api/roadside/provider/notifications/mark-seen`,
        { notification_ids: [notifId] },
        { params: { provider_id: provider.provider_id } }
      )
      .catch(() => {});
  };

  const enableAlerts = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) {
        toast.error('Audio not supported in this browser');
        return;
      }
      if (!audioCtxRef.current) {
        audioCtxRef.current = new Ctx();
      }
      // Resume in case it's suspended (autoplay policy)
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume();
      }
      // Quick test beep so user hears confirmation
      playAlertBeep(audioCtxRef.current);
      setAlertsEnabled(true);
      toast.success('Job alerts enabled');
    } catch (e) {
      toast.error('Could not enable audio alerts');
    }
  };

  const toggleAvailability = async () => {
    try {
      const newStatus = !isAvailable;
      await axios.put(`${BACKEND_URL}/api/roadside/provider/availability`, null, {
        params: { provider_id: provider.provider_id, available: newStatus }
      });

      updateProvider({ is_available: newStatus });
      toast.success(newStatus ? 'You are now available for jobs!' : 'You are now offline');

      if (newStatus && !alertsEnabled) {
        // Prompt to enable alerts immediately when going online
        toast('Tap "Enable Alerts" to hear job ringtones', { duration: 4000 });
      }
    } catch (error) {
      toast.error('Failed to update availability');
      console.error(error);
    }
  };

  const acceptJob = async (requestNumber) => {
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/roadside/provider/accept-job`, null, {
        params: {
          provider_id: provider.provider_id,
          request_number: requestNumber
        }
      });
      toast.success('Job accepted!');
      await fetchData();
    } catch (error) {
      toast.error('Failed to accept job');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const declineJob = async (requestNumber) => {
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/roadside/provider/decline-job`, null, {
        params: {
          provider_id: provider.provider_id,
          request_number: requestNumber
        }
      });
      toast.success('Job declined');
      await fetchData();
    } catch (error) {
      toast.error('Failed to decline job');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateJobStatus = async (status) => {
    setIsLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/roadside/provider/job-status`,
        { request_number: activeJob.request_number, status },
        { params: { provider_id: provider.provider_id } }
      );
      toast.success(`Status updated to ${status}`);
      await fetchData();
      await fetchJobHistory();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/provider/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 pb-safe">
      {/* Persistent in-app job alert banner */}
      {activeAlert && (
        <div
          className="fixed top-0 left-0 right-0 z-[60] bg-red-600 text-white shadow-lg animate-pulse"
          data-testid="job-alert-banner"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <BellRing className="w-6 h-6 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-bold truncate">{activeAlert.title}</p>
                <p className="text-sm text-red-100 truncate">{activeAlert.message}</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={dismissAlert}
              className="bg-white text-red-700 hover:bg-red-50 flex-shrink-0"
              data-testid="dismiss-alert-btn"
            >
              Open
            </Button>
          </div>
        </div>
      )}

      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Provider Dashboard</h1>
              <p className="text-sm text-gray-600">Welcome, {provider?.name}</p>
            </div>
            <div className="flex items-center gap-3">
              {!alertsEnabled && (
                <Button
                  onClick={enableAlerts}
                  variant="outline"
                  className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                  data-testid="enable-alerts-btn"
                >
                  <Volume2 className="w-4 h-4 mr-2" />
                  Tap to enable job alerts
                </Button>
              )}
              {alertsEnabled && (
                <Badge className="bg-green-100 text-green-800 border-green-300 border">
                  <Bell className="w-3 h-3 mr-1" />
                  Alerts on
                </Badge>
              )}
              <Button
                onClick={toggleAvailability}
                className={`${isAvailable ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'} text-white`}
                data-testid="availability-toggle-btn"
              >
                <Power className="w-4 h-4 mr-2" />
                {isAvailable ? 'Available' : 'Offline'}
              </Button>
              <Button onClick={handleLogout} variant="outline" className="border-red-200 text-red-600">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <Card className={`p-6 border-2 ${isAvailable ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Current Status</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="provider-status-label">
                {isAvailable ? 'Available for Jobs' : 'Offline'}
              </p>
            </div>
          </div>
        </Card>

        {activeJob ? (
          <Card className="p-6 border-2 border-blue-200" data-testid="active-job-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">My Schedule - Active Job</h2>
                <p className="text-xs text-gray-500 mt-0.5">#{activeJob.request_number}</p>
              </div>
              <Badge className="bg-blue-100 text-blue-800 border-blue-300">{activeJob.status}</Badge>
            </div>

            {currentLocation && activeJob.customer_location && (
              <div className="h-64 rounded-lg overflow-hidden mb-4">
                <MapContainer
                  center={[activeJob.customer_location.latitude, activeJob.customer_location.longitude]}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker
                    position={[activeJob.customer_location.latitude, activeJob.customer_location.longitude]}
                    icon={customerIcon}
                  >
                    <Popup>Customer Location</Popup>
                  </Marker>
                  {currentLocation && (
                    <Marker position={[currentLocation.latitude, currentLocation.longitude]} icon={providerIcon}>
                      <Popup>Your Location</Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>
            )}

            <div className="space-y-3 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Service Needed</p>
                <p className="font-semibold text-gray-900">{activeJob.service_type}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Vehicle</p>
                <p className="font-semibold text-gray-900">
                  {activeJob.vehicle_year} {activeJob.vehicle_make} {activeJob.vehicle_model}
                </p>
                {activeJob.tire_size && <p className="text-sm text-gray-600">Tire Size: {activeJob.tire_size}</p>}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Location</p>
                <p className="text-sm text-gray-900">
                  {activeJob.customer_location?.address || activeJob.location?.address}
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Customer Contact</p>
                <a href={`tel:${activeJob.customer_phone}`} className="text-blue-600 font-semibold hover:text-blue-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {activeJob.customer_phone}
                </a>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-900 mb-3">Update Job Status:</p>
              <div className="grid grid-cols-2 gap-3">
                {activeJob.status === 'Assigned' && (
                  <Button onClick={() => updateJobStatus('En Route')} disabled={isLoading} className="bg-purple-600 hover:bg-purple-700">
                    <Navigation className="w-4 h-4 mr-2" />
                    En Route
                  </Button>
                )}
                {activeJob.status === 'En Route' && (
                  <Button onClick={() => updateJobStatus('Arrived')} disabled={isLoading} className="bg-orange-600 hover:bg-orange-700">
                    <MapPin className="w-4 h-4 mr-2" />
                    Arrived
                  </Button>
                )}
                {activeJob.status === 'Arrived' && (
                  <Button onClick={() => updateJobStatus('In Progress')} disabled={isLoading} className="bg-indigo-600 hover:bg-indigo-700">
                    <Clock className="w-4 h-4 mr-2" />
                    In Progress
                  </Button>
                )}
                {activeJob.status === 'In Progress' && (
                  <Button onClick={() => updateJobStatus('Completed')} disabled={isLoading} className="bg-green-600 hover:bg-green-700 col-span-2">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Job
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900">My Schedule ({pendingRequests.length})</h2>

            {!isAvailable && (
              <Card className="p-6 bg-yellow-50 border-yellow-200">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                  <p className="text-yellow-800">Turn on availability to receive job requests.</p>
                </div>
              </Card>
            )}

            {pendingRequests.length === 0 ? (
              <Card className="p-12 text-center">
                <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">No pending requests</p>
                <p className="text-sm text-gray-500 mt-2">
                  {isAvailable ? 'Waiting for new job assignments...' : 'Turn on availability to receive jobs'}
                </p>
              </Card>
            ) : (
              pendingRequests.map((request) => (
                <Card key={request.request_number} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{request.service_type}</h3>
                      <p className="text-sm text-gray-600">Request #{request.request_number}</p>
                    </div>
                    <Badge className="bg-yellow-100 text-yellow-800">New Request</Badge>
                  </div>

                  <div className="space-y-2 mb-4">
                    <p className="text-sm">
                      <span className="font-semibold">Vehicle:</span> {request.vehicle_year} {request.vehicle_make} {request.vehicle_model}
                    </p>
                    <p className="text-sm">
                      <span className="font-semibold">Location:</span> {request.location?.address}
                    </p>
                    {request.after_hours && (
                      <Badge className="bg-orange-100 text-orange-800">After Hours (+${request.call_out_fee})</Badge>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button onClick={() => acceptJob(request.request_number)} disabled={isLoading} className="flex-1 bg-green-600 hover:bg-green-700">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Accept
                    </Button>
                    <Button onClick={() => declineJob(request.request_number)} disabled={isLoading} variant="outline" className="flex-1 border-red-200 text-red-600">
                      <XCircle className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Job History Section */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <History className="w-6 h-6 text-gray-700" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Job History</h2>
              <p className="text-sm text-gray-600">
                {statistics ? `${statistics.total_jobs} total jobs • ${statistics.completed_jobs} completed` : 'Loading...'}
              </p>
            </div>
          </div>

          {jobHistory.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No job history yet</p>
              <p className="text-sm text-gray-500 mt-1">Completed jobs will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobHistory.map((job) => (
                <Card key={job.request_number} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-900">#{job.request_number}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          job.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {job.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{job.service_type}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(job.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(job.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600">Customer</p>
                      <p className="font-medium text-gray-900">{job.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Vehicle</p>
                      <p className="font-medium text-gray-900">{job.vehicle_year} {job.vehicle_make}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-600">Location</p>
                      <p className="font-medium text-gray-900">{job.location?.address}</p>
                    </div>
                  </div>

                  {job.completed_at && (
                    <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                      Completed: {new Date(job.completed_at).toLocaleString()}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
